import capabilitiesConfig from '@/configs/permissions/capabilities.json';

// Capability registry (doc 10 §4.3, doc 11 T-1.1). Stable, grouped capability
// keys that compile the legacy permission booleans into one validated unit.
// Every legacy permission key maps to exactly one capability
// (`getLegacyKeyToCapabilityMap`), which is the T-1.1 exit criterion.
//
// Field naming follows doc 10 §4.3: each capability carries an `approval`
// level ('none' | 'member' | 'manager' | 'owner') and a `workspace`
// ('business' | 'branch').

export type CapabilityApproval = 'none' | 'member' | 'manager' | 'owner';
export type CapabilityWorkspace = 'business' | 'branch';

export interface CapabilityDefinition {
  key: string;
  label: string;
  description: string;
  legacyPermissionKeys: string[];
  workspace: CapabilityWorkspace;
  approval: CapabilityApproval;
  branchEligibility: string[];
  retired?: boolean;
}

interface CapabilitiesConfig {
  schemaVersion: number;
  capabilities: CapabilityDefinition[];
}

const config = capabilitiesConfig as unknown as CapabilitiesConfig;

export const CAPABILITY_APPROVAL_VALUES: readonly CapabilityApproval[] = ['none', 'member', 'manager', 'owner'];
export const CAPABILITY_WORKSPACE_VALUES: readonly CapabilityWorkspace[] = ['business', 'branch'];

export function getCapabilities(): CapabilityDefinition[] {
  return [...config.capabilities];
}

export function getCapability(key: string): CapabilityDefinition | undefined {
  return config.capabilities.find((c) => c.key === key);
}

export function getCapabilityKeys(): string[] {
  return config.capabilities.map((c) => c.key);
}

export function getCapabilityByLegacyKey(legacyKey: string): CapabilityDefinition | undefined {
  return config.capabilities.find((c) => c.legacyPermissionKeys.includes(legacyKey));
}

/** T-1.1 exit criterion: every legacy permission key → exactly one capability. */
export function getLegacyKeyToCapabilityMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (const capability of config.capabilities) {
    for (const legacyKey of capability.legacyPermissionKeys) {
      map.set(legacyKey, capability.key);
    }
  }
  return map;
}

export function isCapabilityKey(input: unknown): input is string {
  return typeof input === 'string' && config.capabilities.some((c) => c.key === input);
}

export function isCapabilityApproval(input: unknown): input is CapabilityApproval {
  return typeof input === 'string' && (CAPABILITY_APPROVAL_VALUES as readonly string[]).includes(input);
}

export function isCapabilityWorkspace(input: unknown): input is CapabilityWorkspace {
  return typeof input === 'string' && (CAPABILITY_WORKSPACE_VALUES as readonly string[]).includes(input);
}

export { config as capabilitiesConfig };